import { FC } from 'react'
import { useTranslation } from 'react-i18next'

import { Body1, Body2 } from '@ui/Typography'
import { AlertNotice } from '@ui/components/AlertNotice'
import { Modal } from '@ui/components/Modal'

import { SwapFreeLevelEnum, isRedSwapFreeLevel } from '@model/SwapFreeLevelEnum'
import { TradingAccount } from '@model/TradingAccount'

import { useFormatNumber } from '@hooks/useFormatNumber'

import { Box } from '@shared/components/Box'

import { SwapFreeCountdown } from './SwapFreeCountdown'
import { SwapFreeProgressBar } from './SwapFreeProgressBar'

interface Props {
  tradingAccount: TradingAccount
  onClose?: () => void
}

export const SwapFreeRatingTab: FC<Props> = ({ tradingAccount }) => {
  const { t } = useTranslation()

  const {
    swapFreeLevel,
    swapFreeOption,
    ratingValue,
    swapsSaved,
    isSuspendedFromSwapFree,
    lotsToTrade,
    startRedZone,
  } = tradingAccount

  const { formatMoney } = useFormatNumber()

  const isRedZone = isRedSwapFreeLevel(swapFreeLevel)

  const getLevelStatus = () => {
    switch (swapFreeLevel.id) {
      case SwapFreeLevelEnum.Green:
        return t('Swap Free.Level.High')
      case SwapFreeLevelEnum.Orange:
        return t('Swap Free.Level.Medium')
      case SwapFreeLevelEnum.Red:
        return t('Swap Free.Level.Low')
      default:
        return ''
    }
  }

  const showRedZoneInfo = isRedZone && !isSuspendedFromSwapFree && startRedZone && swapFreeOption

  return (
    <Box flex direction='column' fullWidth gap='x24'>
      <Box flex justify='center'>
        <Body2 color='text-med-em'>{t('Swap Free.Modal.Your Current Rating')}</Body2>
        <Box mx='x4' mt='x4' color='text-med-em'>
          —
        </Box>
        <Body2 semibold>{getLevelStatus()}</Body2>
      </Box>
      {showRedZoneInfo && (
        <SwapFreeCountdown
          startRedZone={startRedZone}
          daysTillSuspended={swapFreeOption?.daysTillSuspended}
          isSuspended={isSuspendedFromSwapFree}
        />
      )}
      {showRedZoneInfo && lotsToTrade !== null && (
        <Box flex justify='center'>
          <Body1 color='text-med-em'>{`${t('Swap Free.Lots to trade')}: ${lotsToTrade}`}</Body1>
        </Box>
      )}
      <Box flex direction='column' gap='x8'>
        <Box flex justify='space-between' align='center' alignSelf='stretch'>
          <Body2 color='text-med-em'>{t('Swap Free.Level.Low')}</Body2>
          <Body2 color='text-med-em'>{t('Swap Free.Level.Medium')}</Body2>
          <Body2 color='text-med-em'>{t('Swap Free.Level.High')}</Body2>
        </Box>
        <SwapFreeProgressBar
          ratingValue={ratingValue}
          red={swapFreeOption?.red ?? 0}
          green={swapFreeOption?.green ?? 0}
          disabled={isSuspendedFromSwapFree}
          expandedRange
        />
      </Box>
      <Box flex direction='column' gap='x16' fullWidth>
        <Box flex fullWidth justify='space-between' color='text-med-em'>
          <Modal.Body.Text>{t('Swap Free.Modal.Saved Swaps')}</Modal.Body.Text>
          <Modal.Body.Text>{`${formatMoney(swapsSaved)}`}</Modal.Body.Text>
        </Box>

        <Box flex direction='column' gap='x16' fullWidth>
          <AlertNotice
            id='swapfree-rating-guide'
            type='info'
            title={t('Swap Free.Modal.Rating Calculation Formula')}
            message={`${t('Swap Free.Modal.Broker Fee')} - ${t(
              'Swap Free.Modal.Swaps Saved'
            )} = ${t('Swap Free.Modal.Tab.Rating')}`}
          />
        </Box>
      </Box>
    </Box>
  )
}
